import { useTranslation } from "react-i18next";
import {
  SIDE_PANEL_MAX_PX,
  SIDE_PANEL_MIN_PX,
  useUIStore,
} from "@/stores/useUIStore";
import { isCompactViewport, useViewportSize } from "@/hooks/useViewportSize";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { CaseNavigator } from "@/components/shell/CaseNavigator";
import { ResizeHandle } from "@/components/shell/ResizeHandle";

export function SidePanel() {
  const { t } = useTranslation();
  const activeMode = useUIStore((s) => s.activeMode);
  const sidePanelCollapsed = useUIStore((s) => s.sidePanelCollapsed);
  const setSidePanelCollapsedByUser = useUIStore((s) => s.setSidePanelCollapsedByUser);
  const toggleSidePanel = useUIStore((s) => s.toggleSidePanel);
  const sidePanelWidth = useUIStore((s) => s.sidePanelWidth);
  const setSidePanelWidth = useUIStore((s) => s.setSidePanelWidth);

  // Phase 7.7 — on mobile + tablet (<1024px), the side panel renders
  // as a fixed-position drawer with a backdrop instead of taking flex
  // space. The hamburger in `<Header>` is the trigger; backdrop click
  // / close button closes it. On desktop (≥1024px) the historic
  // in-flow rendering stays.
  const viewport = useViewportSize();
  const isCompact = isCompactViewport(viewport);

  // Per-mode content registry — anchored to `activeMode`. Modes NOT
  // listed here (chat / agent / agentBrowser) get no SidePanel at all
  // because rendering the 288px <aside> wrapper empty produces a ghost
  // column between the rail and the route content. The short-circuit
  // below skips the wrapper so the route's main column expands to fill
  // the freed space.
  const hasContent =
    activeMode === "home" ||
    activeMode === "cases" ||
    // The global chat thread (`/chat`) shares the same split-pane
    // sidebar as the case routes — the top pane renders the org-scope
    // workspace tree (via `CaseFileNav`'s useEffectiveScope branch)
    // and the bottom pane renders the case navigator (with the "Global"
    // pseudo-row highlighted). Operator brief 2026-07-01.
    activeMode === "chat" ||
    activeMode === "activity" ||
    activeMode === "org" ||
    activeMode === "settings";
  if (!hasContent) return null;

  // COLLAPSED (desktop): render a slim re-open rail instead of nothing.
  // Returning `null` here was the other half of defect #1 — once the panel
  // collapsed there was no element left to click, and the only way back
  // (⌘/Ctrl+B) is documented solely inside the shortcut-help dialog. On compact
  // viewports the panel is a modal drawer whose trigger lives in the header, so
  // a second affordance there would be redundant chrome.
  if (sidePanelCollapsed) {
    if (isCompact) return null;
    return (
      <div
        data-testid="side-panel-reopen-rail"
        className="flex w-6 shrink-0 flex-col items-center border-r border-cream-200/60 bg-white pt-3 dark:border-coffee-700/60 dark:bg-coffee-950"
      >
        <button
          type="button"
          data-testid="side-panel-expand"
          onClick={toggleSidePanel}
          title={t("workspace.sidePanel.expandAriaLabel")}
          aria-label={t("workspace.sidePanel.expandAriaLabel")}
          aria-expanded={false}
          aria-controls="side-panel"
          className="flex h-6 w-6 items-center justify-center rounded text-cream-500 transition-colors hover:bg-cream-100 hover:text-coffee-700 dark:hover:bg-coffee-800 dark:hover:text-cream-200"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    );
  }

  // Workspace-adjacent modes (`home` / `cases` / `chat`) all share the
  // same SidePanel content: a two-pane split with the case-workspace
  // file tree on top and the case navigator (with a "Global" pseudo-row
  // at the top) on the bottom. Operator brief 2026-07-01 — before the
  // refactor, the SidePanel rendered EITHER the workspace tree OR the
  // case selector (mutex), which lost case navigation on /chat and on
  // any case route. `<CaseNavigator/>` now internally does the split.
  const content = (
    <>
      {(activeMode === "home" ||
        activeMode === "cases" ||
        activeMode === "chat") && <CaseNavigator />}
      {activeMode === "activity" && <ActivitySidePanelContent />}
      {activeMode === "org" && <OrgSidePanelContent />}
      {activeMode === "settings" && <SettingsSidePanelContent />}
    </>
  );

  if (isCompact) {
    return (
      <>
        <div
          data-testid="side-panel-backdrop"
          aria-hidden="true"
          onClick={() => setSidePanelCollapsedByUser(true)}
          className="fixed inset-0 z-30 bg-black/40 dark:bg-black/60"
        />
        <aside
          id="side-panel"
          data-testid="side-panel"
          data-layout="drawer"
          aria-label={t("workspace.sidePanel.ariaLabel")}
          className={cn(
            "fixed left-0 top-0 z-40 flex h-full w-72 max-w-[85vw] flex-col border-r border-cream-200/60 bg-white shadow-xl dark:border-coffee-700/60 dark:bg-coffee-950",
            "overflow-hidden",
          )}
        >
          {/* Each per-mode content component carries its own section
              header (just like the rail mode does), so we don't
              double-stack chrome here — only the close button floats
              above the content as a drawer-only affordance. */}
          <button
            type="button"
            data-testid="side-panel-close"
            onClick={() => setSidePanelCollapsedByUser(true)}
            aria-label={t("workspace.sidePanel.closeAriaLabel")}
            className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
          >
            <X size={16} />
          </button>
          <div className="flex flex-1 flex-col overflow-hidden">{content}</div>
        </aside>
      </>
    );
  }

  return (
    <aside
      id="side-panel"
      data-testid="side-panel"
      data-layout="rail"
      aria-label={t("workspace.sidePanel.ariaLabel")}
      // `position: relative` so the absolute-positioned <ResizeHandle/>
      // can anchor to the right edge. Width comes from the persisted
      // store slice; the handle clamps to [SIDE_PANEL_MIN_PX,
      // SIDE_PANEL_MAX_PX] inside setSidePanelWidth.
      style={{ width: sidePanelWidth }}
      className={cn(
        "group/side-panel relative flex shrink-0 flex-col border-r border-cream-200/60 bg-white dark:border-coffee-700/60 dark:bg-coffee-950",
        "overflow-hidden",
      )}
    >
      {content}
      <button
        type="button"
        data-testid="side-panel-collapse"
        onClick={toggleSidePanel}
        title={t("workspace.sidePanel.collapseAriaLabel")}
        aria-label={t("workspace.sidePanel.collapseAriaLabel")}
        aria-expanded
        aria-controls="side-panel"
        // Pinned to the panel's own right edge, vertically centred — the same
        // spot the re-open rail occupies when collapsed, so the control does
        // not appear to move. `z-10` keeps it above the ResizeHandle's hit area.
        className="absolute right-0 top-1/2 z-10 flex h-10 w-4 -translate-y-1/2 items-center justify-center rounded-l border border-r-0 border-cream-200/60 bg-white text-cream-500 opacity-0 transition-opacity hover:text-coffee-700 focus-visible:opacity-100 group-hover/side-panel:opacity-100 dark:border-coffee-700/60 dark:bg-coffee-950 dark:hover:text-cream-200"
      >
        <ChevronLeft size={12} />
      </button>
      <ResizeHandle
        side="right"
        ariaLabel={t("workspace.sidePanel.resizeAriaLabel")}
        ariaValueNow={sidePanelWidth}
        ariaValueMin={SIDE_PANEL_MIN_PX}
        ariaValueMax={SIDE_PANEL_MAX_PX}
        testid="side-panel-resize-handle"
        // Read the LIVE width via getState() — the closure version
        // (`sidePanelWidth`) is frozen at drag-start, so successive
        // pointermove deltas would all accumulate against the start
        // value instead of the running value.
        onResize={(dx) =>
          setSidePanelWidth(useUIStore.getState().sidePanelWidth + dx)
        }
      />
    </aside>
  );
}

function ActivitySidePanelContent() {
  return (
    <div className="flex flex-1 flex-col p-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        Filters
      </h3>
      <p className="text-xs text-slate-400 dark:text-slate-500">Activity filters — Phase 6</p>
    </div>
  );
}

function OrgSidePanelContent() {
  return (
    <div className="flex flex-1 flex-col p-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        Admin
      </h3>
      <p className="text-xs text-slate-400 dark:text-slate-500">Organization admin — Phase 8</p>
    </div>
  );
}

function SettingsSidePanelContent() {
  return (
    <div className="flex flex-1 flex-col p-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        Settings
      </h3>
      <p className="text-xs text-slate-400 dark:text-slate-500">User settings — Phase 7</p>
    </div>
  );
}
